import React from 'react';

type PaginationProps = {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  prevLabel?: string;
  nextLabel?: string;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
  format?: 'slash' | 'of';
  className?: string;
  style?: React.CSSProperties;
  hideWhenSingle?: boolean;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (size: number) => void;
  testId?: string;
};

export default function Pagination({
  page,
  totalPages,
  onPrev,
  onNext,
  prevLabel = 'Prev',
  nextLabel = 'Next',
  prevAriaLabel,
  nextAriaLabel,
  format = 'slash',
  className = 'pagination',
  style,
  hideWhenSingle = true,
  prevDisabled,
  nextDisabled,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  testId,
}: PaginationProps) {
  const showPageSizeSelector = !!(pageSize != null && pageSizeOptions && onPageSizeChange);
  const selectId = React.useId();
  const prevButtonDisabled = prevDisabled || page <= 0 || totalPages <= 0;
  const nextButtonDisabled = nextDisabled || totalPages <= 1 || page >= totalPages - 1;

  if (!showPageSizeSelector && hideWhenSingle && totalPages <= 1) return null;

  let suffix = format === 'of'
        ? ` of ${totalPages}`
        : ` / ${totalPages}`;
  if (!totalPages) {
    suffix = ''
  }
  const status = format === 'of'
      ? `Page ${page + 1}${suffix}`
      : `Page ${page + 1}${suffix}`;

  return (
      <div
        className={showPageSizeSelector ? `${className} pagination-with-size` : className}
        style={style}
        data-testid={testId}
      >
      {showPageSizeSelector && (
        <div className="page-size-selector">
          <label className="page-size-label" htmlFor={selectId}>Items per page</label>
          <select
            id={selectId}
            className="select page-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            data-testid={testId ? `${testId}-page-size` : 'page-size-select'}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}
      <div className="pagination-nav" data-testid={testId ? `${testId}-controls` : undefined}>
        <button
          className="pager-btn"
          disabled={prevButtonDisabled}
          onClick={onPrev}
          aria-label={prevAriaLabel}
          data-testid={testId ? `${testId}-previous` : undefined}
        >
          {prevLabel}
        </button>
        <div className="pager-status" data-testid={testId ? `${testId}-status` : undefined}>{status}</div>
        <button
          className="pager-btn"
          disabled={nextButtonDisabled}
          onClick={onNext}
          aria-label={nextAriaLabel}
          data-testid={testId ? `${testId}-next` : undefined}
        >
          {nextLabel}
        </button>
      </div>
    </div>
  );
}
